import {
    Badge,
    Banner,
    BlockStack,
    Button,
    Collapsible,
    Icon,
    InlineStack,
    Layout,
    Page,
    Select,
    Spinner,
    Text,
} from "@shopify/polaris";
import { ChevronDownIcon, ChevronUpIcon } from "@shopify/polaris-icons";
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import agent from "../../app/api/agent";
import { getApiErrorMessage } from "../../app/api/error";
import { SetupHealth, SetupThemeComponent } from "../../app/models/SetupHealth";
import { getSetupHealthPresentation } from "./setupHealthPresentation";

type HealthRowStatus = "complete" | "action" | "optional" | "recommended" | "unverified" | "not-applicable";

type HealthRowProps = {
    id: string;
    title: string;
    description: string;
    status: HealthRowStatus;
    detail?: string;
    actionLabel?: string;
    onAction?: () => void;
    actionUrl?: string;
};

type SetupGroupProps = {
    id: string;
    title: string;
    description: string;
    stepCount: number;
    children: ReactNode;
};

const healthStatusBadge = (status: HealthRowStatus) => {
    switch (status) {
        case "complete":
            return <Badge tone="success">Complete</Badge>;
        case "action":
            return <Badge tone="attention">Action required</Badge>;
        case "optional":
            return <Badge tone="info">Optional</Badge>;
        case "recommended":
            return <Badge>Recommended</Badge>;
        case "not-applicable":
            return <Badge>Not applicable</Badge>;
        default:
            return <Badge tone="info">Not verified</Badge>;
    }
};

const healthStatusSymbol = (status: HealthRowStatus) => {
    switch (status) {
        case "complete":
            return "✓";
        case "action":
            return "!";
        case "unverified":
            return "i";
        case "not-applicable":
            return "–";
        default:
            return "•";
    }
};

const HealthRow = ({
    id,
    title,
    description,
    status,
    detail,
    actionLabel,
    onAction,
    actionUrl,
}: HealthRowProps) => {
    const [open, setOpen] = useState(false);
    const contentId = `${id}-details`;

    return (
        <div className={`pf-setup-health-step pf-setup-health-step--${status}${open ? " pf-setup-health-step--open" : ""}`}>
            <button
                type="button"
                className="pf-setup-health-step__summary"
                aria-expanded={open}
                aria-controls={contentId}
                onClick={() => setOpen((current) => !current)}
            >
                <span className="pf-setup-health-step__status" aria-hidden="true">
                    {healthStatusSymbol(status)}
                </span>
                <span className="pf-setup-health-step__title">{title}</span>
                <span className="pf-setup-health-step__badge">{healthStatusBadge(status)}</span>
                <span className="pf-setup-health-step__chevron" aria-hidden="true">
                    <Icon source={open ? ChevronUpIcon : ChevronDownIcon} />
                </span>
            </button>
            <Collapsible id={contentId} open={open} transition={{ duration: "150ms", timingFunction: "ease" }}>
                <div className="pf-setup-health-step__details">
                    <div className="pf-setup-health-step__copy">
                        <Text as="p" variant="bodySm" tone="subdued">{description}</Text>
                        {detail ? <Text as="p" variant="bodySm" tone="subdued">{detail}</Text> : null}
                    </div>
                    {actionLabel && (onAction || actionUrl) ? (
                        <div className="pf-setup-health-step__action">
                            <Button
                                onClick={onAction}
                                url={actionUrl || undefined}
                                target={actionUrl ? "_blank" : undefined}
                            >
                                {actionLabel}
                            </Button>
                        </div>
                    ) : null}
                </div>
            </Collapsible>
        </div>
    );
};

const SetupGroup = ({ id, title, description, stepCount, children }: SetupGroupProps) => {
    const [open, setOpen] = useState(true);
    const contentId = `${id}-content`;

    return (
        <section className="pf-setup-health-group">
            <button
                type="button"
                className="pf-setup-health-group__header"
                aria-expanded={open}
                aria-controls={contentId}
                onClick={() => setOpen((current) => !current)}
            >
                <span className="pf-setup-health-group__heading">
                    <span className="pf-setup-health-group__title">{title}</span>
                    <span className="pf-setup-health-group__description">{description}</span>
                </span>
                <span className="pf-setup-health-group__count">{stepCount} steps</span>
                <span className="pf-setup-health-group__chevron" aria-hidden="true">
                    <Icon source={open ? ChevronUpIcon : ChevronDownIcon} />
                </span>
            </button>
            <Collapsible id={contentId} open={open} transition={{ duration: "180ms", timingFunction: "ease" }}>
                <div className="pf-setup-health-group__content">{children}</div>
            </Collapsible>
        </section>
    );
};

const getComponentStatus = (
    component: SetupThemeComponent,
    inspectionAvailable: boolean,
): HealthRowStatus => {
    if (!component.applicable) return "not-applicable";
    if (!inspectionAvailable || component.installed == null) return "unverified";
    if (component.installed) return "complete";
    return component.required ? "action" : "optional";
};

const SetupHealthPage = () => {
    const navigate = useNavigate();
    const [health, setHealth] = useState<SetupHealth>();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>();

    const loadHealth = useCallback(async (themeId?: string) => {
        setLoading(true);
        setError(undefined);

        try {
            setHealth(await agent.Settings.setupHealth(themeId));
        } catch (loadError) {
            setError(await getApiErrorMessage(loadError, "Unable to check PartFits setup."));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void loadHealth();
    }, [loadHealth]);

    const presentation = useMemo(
        () => health ? getSetupHealthPresentation(health.overallStatus) : undefined,
        [health],
    );
    const themeOptions = health?.themes.map((theme) => ({
        label: `${theme.name}${theme.isPublished ? " — Published" : ""}`,
        value: theme.id,
    })) ?? [];
    const selectedThemeNeedsManualInstall = health?.selectedTheme?.supportsJsonTemplates === false;
    const manualInstallSupportUrl = health?.selectedTheme
        ? `mailto:support@govedia.com?subject=${encodeURIComponent("PartFits legacy theme installation assistance")}&body=${encodeURIComponent(
            `Shop: ${health.shopDomain}\nTheme: ${health.selectedTheme.name}\n\nPlease help us complete a manual PartFits installation for this legacy Shopify theme.`,
        )}`
        : "mailto:support@govedia.com?subject=PartFits%20legacy%20theme%20installation%20assistance";
    const coreCompleteCount = health
        ? [
            health.core.enabledFieldCount > 0,
            health.core.fitmentRecordCount > 0 || health.core.referenceDataEnabled,
            health.core.productFitmentAssignmentCount > 0 || health.core.externalProductLinkCount > 0 || health.core.universalProductCount > 0,
            health.core.storefrontConfigured && health.core.storefrontEnabled,
        ].filter(Boolean).length
        : 0;

    return (
        <Page
            title="Setup & Health"
            subtitle="Configure PartFits, verify the selected Shopify theme, and catch storefront issues after theme changes."
            primaryAction={{
                content: "Recheck setup",
                loading,
                onAction: () => loadHealth(health?.selectedThemeId),
            }}
        >
            <Layout>
                <Layout.Section>
                    <BlockStack gap="500">
                        {error ? (
                            <Banner tone="critical" title="Setup health could not be loaded">
                                <BlockStack gap="200">
                                    <Text as="p">{error}</Text>
                                    <InlineStack>
                                        <Button onClick={() => loadHealth(health?.selectedThemeId)}>Try again</Button>
                                    </InlineStack>
                                </BlockStack>
                            </Banner>
                        ) : null}

                        {!health && loading ? (
                            <div className="pf-setup-health-card pf-setup-health-loading">
                                <InlineStack gap="200" blockAlign="center" align="center">
                                    <Spinner size="small" accessibilityLabel="Checking PartFits setup" />
                                    <Text as="p" tone="subdued">Checking your data and Shopify theme…</Text>
                                </InlineStack>
                            </div>
                        ) : null}

                        {health && presentation ? (
                            <>
                                <section className={`pf-setup-health-summary pf-setup-health-summary--${presentation.tone}`}>
                                    <div className="pf-setup-health-summary__header">
                                        <span className="pf-setup-health-summary__icon" aria-hidden="true">
                                            {presentation.tone === "success" ? "✓" : presentation.tone === "warning" ? "!" : "i"}
                                        </span>
                                        <Text as="h2" variant="headingSm">{presentation.title}</Text>
                                    </div>
                                    <div className="pf-setup-health-summary__body">
                                        <div className="pf-setup-health-summary__copy">
                                            <Text as="p">{health.overallMessage}</Text>
                                            {health.selectedTheme && !health.selectedTheme.isPublished ? (
                                                <Text as="p" variant="bodySm" tone="subdued">
                                                    You are checking the draft theme “{health.selectedTheme.name}”. The published storefront can have different blocks.
                                                </Text>
                                            ) : null}
                                        </div>
                                        <InlineStack gap="200" wrap>
                                            {health.selectedTheme?.previewUrl ? (
                                                <Button url={health.selectedTheme.previewUrl} target="_blank">Preview storefront</Button>
                                            ) : null}
                                            {health.selectedTheme?.manageUrl ? (
                                                <Button url={health.selectedTheme.manageUrl} target="_blank">Open Theme Editor</Button>
                                            ) : null}
                                        </InlineStack>
                                    </div>
                                </section>

                                <section className="pf-setup-health-card">
                                    <div className="pf-setup-health-card__header">
                                        <Text as="h2" variant="headingMd">Get live with PartFits</Text>
                                        <Text as="p" variant="bodySm" tone="subdued">
                                            Work through these steps to configure the app and confirm it is installed on your theme.
                                        </Text>
                                    </div>

                                    <SetupGroup
                                        id="core-setup"
                                        title="Core setup"
                                        description="Data and behavior PartFits needs before storefront components can work."
                                        stepCount={4}
                                    >
                                        <HealthRow
                                            id="configure-fitment-fields"
                                            title="Configure fitment fields"
                                            description="Choose the fields shoppers use to identify a compatible vehicle or machine."
                                            detail={`${health.core.enabledFieldCount.toLocaleString()} visible selector fields configured`}
                                            status={health.core.enabledFieldCount > 0 ? "complete" : "action"}
                                            actionLabel="Manage fields"
                                            onAction={() => navigate("/storefront?section=selector-fields")}
                                        />
                                        <HealthRow
                                            id="add-fitment-records"
                                            title="Add fitment records"
                                            description="Fitment records provide the selectable vehicles or machines in the storefront search."
                                            detail={`${health.core.fitmentRecordCount.toLocaleString()} active fitment records${health.core.referenceDataEnabled ? " · reference data enabled" : ""}`}
                                            status={health.core.fitmentRecordCount > 0 || health.core.referenceDataEnabled ? "complete" : "action"}
                                            actionLabel={health.core.fitmentRecordCount > 0 || health.core.referenceDataEnabled ? "View records" : "Import data"}
                                            onAction={() => navigate(health.core.fitmentRecordCount > 0 || health.core.referenceDataEnabled ? "/vehicles" : "/imports")}
                                        />
                                        <HealthRow
                                            id="connect-products"
                                            title="Connect products to fitments"
                                            description="Map products to fitment records or mark products as universal."
                                            detail={`${health.core.productFitmentAssignmentCount.toLocaleString()} fitment assignments · ${health.core.externalProductLinkCount.toLocaleString()} external product links · ${health.core.universalProductCount.toLocaleString()} universal products · ${health.core.syncedProductCount.toLocaleString()} synced products`}
                                            status={health.core.productFitmentAssignmentCount > 0 || health.core.externalProductLinkCount > 0 || health.core.universalProductCount > 0 ? "complete" : "action"}
                                            actionLabel="Manage products"
                                            onAction={() => navigate("/products")}
                                        />
                                        <HealthRow
                                            id="configure-storefront"
                                            title="Configure storefront behavior"
                                            description="Choose how searches run and how matching products are displayed."
                                            detail={`Current result mode: ${health.renderingMode}`}
                                            status={health.core.storefrontConfigured && health.core.storefrontEnabled ? "complete" : "action"}
                                            actionLabel="Open settings"
                                            onAction={() => navigate("/storefront")}
                                        />
                                    </SetupGroup>

                                    <SetupGroup
                                        id="theme-setup"
                                        title="Theme setup"
                                        description="These steps apply to the selected Shopify theme."
                                        stepCount={health.components.length + 1}
                                    >
                                        {themeOptions.length > 0 ? (
                                            <div className="pf-setup-health-theme-select">
                                                <div className="pf-setup-health-theme-select__copy">
                                                    <Text as="h3" variant="headingSm">Select theme</Text>
                                                    <Text as="p" variant="bodySm" tone="subdued">
                                                        {health.selectedTheme
                                                            ? `${health.selectedTheme.name}${health.selectedTheme.isPublished ? " · Active theme" : " · Draft theme"}`
                                                            : "Choose the theme you want to verify."}
                                                    </Text>
                                                </div>
                                                <div className="pf-setup-health-theme-select__control">
                                                    <Select
                                                        label="Select theme"
                                                        labelHidden
                                                        options={themeOptions}
                                                        value={health.selectedThemeId}
                                                        disabled={loading}
                                                        onChange={(value) => loadHealth(value)}
                                                    />
                                                </div>
                                            </div>
                                        ) : null}

                                        {health.themeInspectionWarning ? (
                                            <div className="pf-setup-health-theme-notice">
                                                <Banner
                                                    tone={health.themeReadPermissionGranted === false ? "warning" : "info"}
                                                    title={health.themeReadPermissionGranted === false
                                                        ? "Theme read permission required"
                                                        : "Theme verification is limited"}
                                                >
                                                    <Text as="p">{health.themeInspectionWarning}</Text>
                                                </Banner>
                                            </div>
                                        ) : null}

                                        {health.selectedTheme && !health.selectedTheme.supportsJsonTemplates ? (
                                            <div className="pf-setup-health-theme-notice">
                                                <Banner tone="warning" title="This theme may need a manual installation">
                                                    <BlockStack gap="300">
                                                        <Text as="p">
                                                            PartFits did not find Shopify JSON templates in this theme. You can use an Online Store 2.0 theme, or contact us for help installing PartFits directly in this legacy theme’s code. We may ask for Shopify collaborator access before making approved theme changes.
                                                        </Text>
                                                        <InlineStack>
                                                            <Button url={manualInstallSupportUrl}>Contact PartFits support</Button>
                                                        </InlineStack>
                                                    </BlockStack>
                                                </Banner>
                                            </div>
                                        ) : null}

                                        {health.components.map((component) => {
                                            const status = getComponentStatus(component, health.themeInspectionAvailable);
                                            const detail = selectedThemeNeedsManualInstall && component.applicable
                                                ? component.required
                                                    ? "Manual installation is required for this legacy theme"
                                                    : "This component can be included during an assisted manual installation"
                                                : component.locations.length > 0
                                                ? `Found in ${component.locations.join(", ")}`
                                                : component.required
                                                    ? "Required for the current PartFits configuration"
                                                    : component.applicable
                                                        ? "Optional storefront component"
                                                        : "Not required by the current search result mode";
                                            const actionLabel = selectedThemeNeedsManualInstall
                                                ? undefined
                                                : component.installed
                                                    ? "Manage"
                                                    : component.actionUrl
                                                        ? "Add to theme"
                                                        : undefined;

                                            return (
                                                <HealthRow
                                                    key={component.key}
                                                    id={`theme-component-${component.key}`}
                                                    title={component.label}
                                                    description={component.description}
                                                    detail={detail}
                                                    status={status}
                                                    actionLabel={actionLabel}
                                                    actionUrl={selectedThemeNeedsManualInstall
                                                        ? undefined
                                                        : component.installed
                                                            ? component.manageUrl
                                                            : component.actionUrl}
                                                />
                                            );
                                        })}

                                        <HealthRow
                                            id="verify-customer-experience"
                                            title="Preview and test the customer experience"
                                            description="Choose a real mapped vehicle in the selected theme and confirm that the expected products appear."
                                            detail="This final check is completed by you in the storefront preview."
                                            status="recommended"
                                            actionLabel={health.selectedTheme?.previewUrl ? "Preview and test" : "Recheck setup"}
                                            actionUrl={health.selectedTheme?.previewUrl || undefined}
                                            onAction={health.selectedTheme?.previewUrl ? undefined : () => loadHealth(health.selectedThemeId)}
                                        />
                                    </SetupGroup>
                                </section>

                                <div className="pf-setup-health-footnote">
                                    <Text as="p" variant="bodySm" tone="subdued">
                                        {coreCompleteCount} of 4 core steps complete. Recheck after publishing a theme or changing app blocks.
                                    </Text>
                                </div>
                            </>
                        ) : null}
                    </BlockStack>
                </Layout.Section>
            </Layout>
        </Page>
    );
};

export default SetupHealthPage;
